import { useEffect, useRef, useState, type ReactNode } from "react";

import clsx from "clsx";
import { useElementSize } from "usehooks-ts";

import { Tooltip } from "@components/tooltip-container";
import { Menu, MenuButton, MenuItem, MenuItems } from "@headlessui/react";
import { renderChevronAnimation } from "@shared/selects/utils";
import { generateAnonBase64Hash } from "@utils/anonymous";
import { usePrefs } from "@utils/client-side-preferences";

import { QuickFilterButton } from "./quick-filter-button";
import { RestMenuButton } from "./rest-menu-button";

interface Option {
  id: string | number;
  name: string;
  isDefault?: boolean;
}

interface Props<T> {
  options: T[];
  value: T;
  onChange: (filter: T) => void;
  tooltipComponent?: (filter: T) => ReactNode;
}

export const QuickFilterSelector = <T extends Option>({
  options,
  value,
  onChange,
  tooltipComponent,
}: Props<T>) => {
  const measurementContainer = useRef<HTMLDivElement>(null);
  const [containerRef, { width: containerWidth }] = useElementSize();
  const { isDemoMode } = usePrefs();

  const [visibleItemsCount, setVisibleItemsCount] = useState(1);

  useEffect(() => {
    if (!measurementContainer.current) {
      setVisibleItemsCount(1);
      return;
    }

    let totalChilElWidth = 0;
    let visibleItems = -1;

    for (const childEl of measurementContainer.current.children) {
      totalChilElWidth += childEl.getBoundingClientRect().width + 4;

      if (totalChilElWidth > containerWidth) {
        break;
      }

      visibleItems += 1;
    }

    setVisibleItemsCount(visibleItems);
  }, [options, containerWidth]);

  const visibleOptions = options.slice(0, visibleItemsCount);
  const restOptions = options.slice(visibleItemsCount);
  const isRestSelected =
    restOptions.find((option) => option.id === value.id) !== undefined;

  return (
    <div className="flex flex-grow items-center gap-2 overflow-hidden">
      <div
        ref={containerRef}
        className="relative flex flex-grow items-center gap-1 whitespace-nowrap overflow-hidden"
      >
        {/* hidden container used for measurements */}
        <div
          ref={measurementContainer}
          className={clsx(
            "absolute left-0 right-0 top-0 invisible pointer-events-none",
            "flex flex-grow items-center gap-1",
          )}
        >
          <RestMenuButton isActive={isRestSelected}>
            {isRestSelected ? value.name : `More (${restOptions.length})`}
            {renderChevronAnimation(false, false, undefined, "ml-2")}
          </RestMenuButton>
          {options.map((option) => {
            return (
              <QuickFilterButton
                key={option.id}
                name={generateAnonBase64Hash({
                  value: option.name,
                  prefix: "quick-filter",
                  doAnon: isDemoMode && !option.isDefault,
                })}
                isSelected={option.id === value.id}
                tooltipComponent={tooltipComponent?.(option)}
                onClick={() => onChange(option)}
              />
            );
          })}
        </div>
        {visibleOptions.map((option) => {
          return (
            <QuickFilterButton
              key={option.id}
              name={generateAnonBase64Hash({
                value: option.name,
                prefix: "quick-filter",
                doAnon: isDemoMode && !option.isDefault,
              })}
              isSelected={option.id === value.id}
              tooltipComponent={tooltipComponent?.(option)}
              onClick={() => onChange(option)}
            />
          );
        })}
        {restOptions.length > 0 && (
          <Menu as="div">
            <MenuButton as={RestMenuButton} isActive={isRestSelected}>
              {({ open }) => (
                <Tooltip
                  noInfoCircle
                  overlayClassName="bg-v2-secondary"
                  content={tooltipComponent?.(value)}
                  showTooltip={!!tooltipComponent && isRestSelected && !open}
                >
                  {isRestSelected
                    ? generateAnonBase64Hash({
                        value: value.name,
                        prefix: "quick-filter",
                        doAnon: isDemoMode && !value.isDefault,
                      })
                    : `More (${restOptions.length})`}
                  {renderChevronAnimation(open, false, undefined, "ml-2")}
                </Tooltip>
              )}
            </MenuButton>
            <MenuItems
              anchor={{ to: "bottom end", gap: 4 }}
              className="bg-v2-primary z-[99] shadow rounded-md max-h-[480px]"
            >
              {restOptions.map((option) => (
                <MenuItem
                  key={option.id}
                  as="div"
                  className={clsx(
                    "h-12 px-3.5 text-sm text-v2-action font-semibold flex items-center",
                    "tracking-wide hover:bg-v2-action-secondary cursor-pointer",
                  )}
                  onClick={() => {
                    onChange(option);
                  }}
                >
                  {generateAnonBase64Hash({
                    value: option.name,
                    prefix: "quick-filter",
                    doAnon: isDemoMode && !option.isDefault,
                  })}
                </MenuItem>
              ))}
            </MenuItems>
          </Menu>
        )}
      </div>
    </div>
  );
};
